import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { ReptoolModal } from "../../utilities/ReptoolModal.tsx";
import { BasicFetch } from "../../../utils/BasicFetch.ts";
import { Clutch } from '../../../types/Clutch';
import { Gene } from '../../../types/Gene.ts';
import { FindMorphByGenes } from '../../utilities/FindMorph.ts';
import { LoadingSpinner } from '../../Loading.tsx';
import { GeneText } from '../../utilities/GeneText.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { makeMorphNameStringPotential } from '../../../utils/GeneCalculator.ts';
import { Morph } from '../../../types/Morph.ts';
import { useRepToolContext } from '../../../RepToolContext.tsx';
interface Props {
  closeModal: () => void;
  onUpdate?: () => void;
  editingClutch: Clutch;
  provenGenes: Gene[];
}

const UpdateGenetics = ({ editingParentId, genes, onSuccess, morphs }: {
  editingParentId: number,
  genes: Gene[],
  onSuccess: () => void,
  morphs: Morph[]
}) => {
  BasicFetch({
    url: `${(window as any).serverLocation}/collection_reptile/${editingParentId}/edit_morph`,
    method: 'POST',
    contentType: 'application/json',
    data: {
      morph_name: makeMorphNameStringPotential(genes, morphs),
      genes: genes.map(gene => {
        return {
          ...gene,
          id: gene.gene_id,
          expression: gene.expression,
        }
      }),
    },
    onSuccess,
    onFail: () => {
      alert('Error updating genes');
    },
  })
}


const ParentItem = ({ parent, provenGenes, morphs }: {
  parent: {
    genes: Gene[],
    id: number
  },
  provenGenes: Gene[],
  morphs: Morph[]
}) => {
  const [pictureLoaded, setPictureLoaded] = React.useState(false);
  const morph = FindMorphByGenes(parent.genes, morphs);
  const picture = morph?.pictures?.[0];
  return <div className='col-md-4 col-5'>
    <div className='d-flex justify-content-between'>
      <div className="male p-0 mx-auto flex-grow parent">
        <img id="male-picture" className={`default-image w-100 ${!pictureLoaded ? 'd-none' : ''}`} src={picture?.url} onLoad={() => setPictureLoaded(true)} />
        <div className={`default-image ${pictureLoaded ? 'd-none' : ''}`}>
          <LoadingSpinner />
        </div>
        {pictureLoaded && <h6 id="male-credit" className="calculator-credit">{picture?.credit}</h6>}
        <div>
          {
            parent.genes.map((gene) => {
              const isProven = provenGenes.some(provenGene => provenGene.id === gene.id);
              return (
                <div key={gene.id} className={`d-flex justify-content-between ${isProven ? 'text-warning' : ''}`}>
                  <GeneText gene={gene} />
                </div>
              )
            })
          }
        </div>
      </div>
    </div>
  </div>
}

const UpdateParentItem = ({ parent, provenGenes }) => {
  const edittedParent = {
    ...parent, genes: parent.genes.map(gene => {
      return {
        ...gene,
        expression: provenGenes.find(provenGene => provenGene.id === gene.id) ? "HET" : gene.expression
      }
    })
  };

  return (
    <div className='d-flex flex-row justify-content-between'>
      <ParentItem parent={parent} provenGenes={provenGenes} />
      <div className='h-100 flex-fill m-auto'>
        <div className='text-center'>
          <FontAwesomeIcon size='2x' icon={faArrowRight} />
        </div>
      </div>
      <ParentItem parent={edittedParent} provenGenes={provenGenes} />
    </div>
  )
}


export const UpdateGeneticsModal: React.FC<Props> = ({ onUpdate, closeModal, editingClutch, provenGenes }) => {
  const [saving, setSaving] = React.useState(false);
  const { appendControls } = useRepToolContext();

  const updateGenetics = () => {
    // promise all the updates
    const promises: Promise<() => void>[] = [];

    if (provenGenes.some(gene => {
      return editingClutch.father.genes.some(fatherGene => fatherGene.id === gene.id);
    })) {
      promises.push(new Promise((resolve, reject) => {
        UpdateGenetics({
          editingParentId: editingClutch.father.id,
          genes: editingClutch.father.genes.map(gene => {
            return {
              ...gene,
              reptile: editingClutch.father.reptile,
              expression: provenGenes.find(provenGene => provenGene.id === gene.id) ? "HET" : gene.expression
            }
          }),
          onSuccess: resolve,
        })
      }));
    }
    if (provenGenes.some(gene => {
      return editingClutch.mother.genes.some(motherGene => motherGene.id === gene.id);
    }
    )) {
      promises.push(new Promise((resolve, reject) => {
        UpdateGenetics({
          editingParentId: editingClutch.mother.id,
          genes: editingClutch.mother.genes.map(gene => {
            return {
              ...gene,
              reptile: editingClutch.mother.reptile,
              expression: provenGenes.find(provenGene => provenGene.id === gene.id) ? "HET" : gene.expression
            }
          }),
          onSuccess: resolve,
        })
      }));
    }

    Promise.all(promises).then(() => {
      setSaving(false);
      closeModal();
      onUpdate?.();
    })

  }

  useEffect(() => {
    if (saving) {
      updateGenetics();
    }
  }, [saving]);


  useEffect(() => {
    appendControls(<div className="text-center w-100">
      <span className="d-flex justify-content-center">
        <div>
          <Button variant="outline-light" disabled={saving} onClick={closeModal} className="mr-3">Cancel</Button>
          <Button variant="outline-light" disabled={saving} onClick={() => setSaving(true)}>Update</Button>
        </div>
      </span>
    </div>, 'Update Genetics Modal');
  }, []);
  const isFatherProven = provenGenes.some(gene => {
    return editingClutch.father.genes.some(fatherGene => fatherGene.id === gene.id);
  });
  const isMotherProven = provenGenes.some(gene => {
    return editingClutch.mother.genes.some(motherGene => motherGene.id === gene.id);
  });
  return (
    <ReptoolModal id='update-genetics-modal' title={'Genetics Proven'} closeModal={closeModal} body={
      <>
        <div className=''>
          <p>
            Congrats! It looks like you have proven the genetics on one or more of the parents of this clutch. Would you like to update the genetics of the parents?
          </p>
        </div>
        {
          isFatherProven && <>
            <h5 className='mt-3'>Sire:</h5>
            <UpdateParentItem parent={editingClutch.father} provenGenes={provenGenes} />
          </>
        }
        {
          isMotherProven && <>
            <h5 className='mt-3'>Dam:</h5>
            <UpdateParentItem parent={editingClutch.mother} provenGenes={provenGenes} />
          </>
        }
      </>
    }>
    </ReptoolModal>
  );
};

export default UpdateGeneticsModal;
